import { ErrorInfo } from "react";
import { useLogError } from "../hooks";

// Function to extract component name from component stack
export function extractComponentName(componentStack: string): string {
  if (!componentStack) return "Unknown Component";

  // Try to extract component name from the stack
  // Component stack format: "in ComponentName (at file:line:column)"
  const componentMatch = componentStack.match(/in\s+([A-Z][a-zA-Z0-9]*)/);
  if (componentMatch && componentMatch[1]) {
    return componentMatch[1];
  }

  // Try to extract from function names
  const functionMatch = componentStack.match(/at\s+([A-Z][a-zA-Z0-9]*)/);
  if (functionMatch && functionMatch[1]) {
    return functionMatch[1];
  }

  // Try to extract from file paths
  const fileMatch = componentStack.match(/\/([^/]+)\.(tsx?|jsx?)/);
  if (fileMatch && fileMatch[1]) {
    return fileMatch[1];
  }

  // Fallback to first meaningful part
  const lines = componentStack.split("\n");
  for (const line of lines) {
    if (line.includes("in ") || line.includes("at ")) {
      const parts = line.split(/in\s+|at\s+/);
      if (parts.length > 1 && parts[1]) {
        const component = parts[1].split(/[\s(]/)[0];
        if (component && /^[A-Z]/.test(component)) {
          return component;
        }
      }
    }
  }

  return "Unknown Component";
}

// Function to get more detailed error location information
export function getErrorLocationInfo(
  error: Error,
  componentStack: string,
): {
  componentName: string;
  filePath?: string;
  lineNumber?: string;
  errorType: string;
} {
  const componentName = extractComponentName(componentStack);

  // Try to extract file path and line number from stack
  let filePath: string | undefined;
  let lineNumber: string | undefined;

  if (error.stack) {
    const stackLines = error.stack.split("\n");
    for (const line of stackLines) {
      // Look for file paths in the stack
      const fileMatch = line.match(/\(([^)]+\.(tsx?|jsx?)):(\d+):\d+\)/);
      if (fileMatch) {
        filePath = fileMatch[1];
        lineNumber = fileMatch[3];
        break;
      }
    }
  }

  return {
    componentName,
    filePath,
    lineNumber,
    errorType: error.constructor.name,
  };
}

// Error categorization function
function categorizeReactError(error: Error): {
  category: string;
  subcategory?: string;
  isCommon: boolean;
  suggestedFix?: string;
} {
  const message = error.message;
  const stack = error.stack || "";

  // React child validation errors
  if (message.includes("Objects are not valid as a React child")) {
    return {
      category: "React Child Validation",
      subcategory: "Invalid Child Type",
      isCommon: true,
      suggestedFix:
        "Check if you're trying to render an Error object or other non-serializable object directly in JSX. Convert to string or handle properly.",
    };
  }

  // Hook errors
  if (message.includes("React Hook") && message.includes("called")) {
    return {
      category: "React Hooks",
      subcategory: "Hook Rules Violation",
      isCommon: true,
      suggestedFix:
        "Ensure hooks are called at the top level of React functions, not inside loops, conditions, or nested functions.",
    };
  }

  // State update errors
  if (
    message.includes("Cannot update a component") &&
    message.includes("while rendering")
  ) {
    return {
      category: "State Management",
      subcategory: "State Update During Render",
      isCommon: true,
      suggestedFix:
        "Move state updates to useEffect or event handlers. Don't update state during render phase.",
    };
  }

  // Component lifecycle errors
  if (
    message.includes("componentWillReceiveProps") ||
    message.includes("componentWillMount")
  ) {
    return {
      category: "Component Lifecycle",
      subcategory: "Deprecated Lifecycle",
      isCommon: false,
      suggestedFix: "Update to use modern React lifecycle methods or hooks.",
    };
  }

  // Memory leaks
  if (
    message.includes(
      "Can't perform a React state update on an unmounted component",
    )
  ) {
    return {
      category: "Memory Management",
      subcategory: "Memory Leak",
      isCommon: true,
      suggestedFix:
        "Add cleanup in useEffect return function or check if component is mounted before state updates.",
    };
  }

  // Context errors
  if (message.includes("useContext") && message.includes("undefined")) {
    return {
      category: "React Context",
      subcategory: "Context Not Provided",
      isCommon: true,
      suggestedFix:
        "Ensure the component is wrapped with the appropriate Context Provider.",
    };
  }

  // Ref errors
  if (
    message.includes("ref") &&
    (message.includes("null") || message.includes("undefined"))
  ) {
    return {
      category: "React Refs",
      subcategory: "Ref Access Error",
      isCommon: true,
      suggestedFix:
        "Check if ref is properly initialized and not null before accessing its properties.",
    };
  }

  // Generic React errors
  if (message.includes("React") || stack.includes("react-dom")) {
    return {
      category: "React Framework",
      subcategory: "Generic React Error",
      isCommon: true,
      suggestedFix:
        "Review React best practices and check component implementation.",
    };
  }

  // JavaScript errors
  if (
    message.includes("TypeError") ||
    message.includes("ReferenceError") ||
    message.includes("SyntaxError")
  ) {
    return {
      category: "JavaScript Runtime",
      subcategory: "Runtime Error",
      isCommon: true,
      suggestedFix:
        "Check variable declarations, function calls, and object property access.",
    };
  }

  // Network/API errors
  if (
    message.includes("fetch") ||
    message.includes("HTTP") ||
    message.includes("Network")
  ) {
    return {
      category: "Network/API",
      subcategory: "Request Error",
      isCommon: true,
      suggestedFix:
        "Check network connectivity, API endpoints, and request parameters.",
    };
  }

  return {
    category: "Unknown",
    subcategory: "Uncategorized Error",
    isCommon: false,
    suggestedFix: "Review error message and stack trace for debugging.",
  };
}

// HOC to add error logging to the error boundary
export function withErrorLogging<P extends object>(
  WrappedComponent: React.ComponentType<P>,
): React.ComponentType<
  P & { onError?: (error: Error, errorInfo: ErrorInfo) => void }
> {
  return function WithErrorLoggingComponent(
    props: P & { onError?: (error: Error, errorInfo: ErrorInfo) => void },
  ) {
    const { logError } = useLogError();
    const handleError = (error: Error, errorInfo: ErrorInfo) => {
      const errorCategory = categorizeReactError(error);
      const locationInfo = getErrorLocationInfo(
        error,
        errorInfo.componentStack || "",
      );

      logError(error, {
        type: "errorBoundary",
        errorBoundary: true,
        errorCategory: errorCategory.category,
        errorSubcategory: errorCategory.subcategory,
        isCommonError: errorCategory.isCommon,
        suggestedFix: errorCategory.suggestedFix,
        componentStack: errorInfo.componentStack,
        // Detailed component and location information
        errorComponent: locationInfo.componentName,
        errorFile: locationInfo.filePath,
        errorLine: locationInfo.lineNumber,
        errorType: locationInfo.errorType,
        // Additional error boundary context
        errorBoundaryInfo: {
          componentStack: errorInfo.componentStack,
          errorBoundary: "AppErrorBoundary",
          timestamp: new Date().toISOString(),
        },
      });

      // Call custom onError handler if provided
      if (props.onError) {
        props.onError(error, errorInfo);
      }
    };

    return <WrappedComponent {...props} onError={handleError} />;
  };
}
